/* eslint-disable @typescript-eslint/no-explicit-any */

import { ColumnDef } from '@tanstack/react-table';
import { useQuery } from 'react-query';
import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Offcanvas } from 'react-bootstrap';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import reportInvoiceService from 'service/reportInvoice';
import { pageToOffset } from 'utils/pageToOffset';
import Button from 'components/base/Button';
import ShowSerialNumber from '../purchase/ShowSerialNumber';
import { fieldMappingRemainder } from 'data/fieldMaping';

export type RemainderData = {
  product_name: string;
  barcode: string;
  currency: string;
  unit: string;
  category: string;
  cost: string;
  quantity: string;
  sum: string;
  serial_number: any;
};

interface RemainderTableProps {
  generateTableData?: any;
  setGeneratedTableData?: any;
  getName?: any;
  getBarcode?: any;
  getFilterData?: any;
  savedValues?: any;
  setTotalQuantity?: any;
  setTotalincome?: any;
  setTotalSale?: any;
  currentPage?: any;
  setCurrentPage?: any;
  pageCount?: any;
  setPageCount?: any;
  // barcodeInputRef?: any;
  debouncedBarcode?: any;
  debouncedSearchValue?: any;
  setLabeledVisibleFields?: any;
}

const RemainderTable = ({
  generateTableData,
  setGeneratedTableData,
  getName,
  getBarcode,
  getFilterData,
  savedValues,
  setTotalQuantity,
  setTotalincome,
  setTotalSale,
  currentPage,
  setCurrentPage,
  pageCount,
  setPageCount,
  // barcodeInputRef,
  debouncedBarcode,
  debouncedSearchValue,
  setLabeledVisibleFields
}: RemainderTableProps) => {
  const { t, i18n } = useTranslation();
  const currencyId = useSelector(
    (state: any) => state?.auth?.legal_entity?.currency_pid
  );

  const [hasReset, setHasReset] = useState(false);
  const [showSerialNumber, setShowSerialNumber] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const branchId = useSelector((state: any) => state?.auth?.legal_entity?.pid);
  const branchName = useSelector(
    (state: any) => state?.auth?.legal_entity?.name
  );

  // Memoized onVisibleFieldsChange callback with shallow comparison
  const handleVisibleFieldsChange = useCallback(
    (visibleFields: { [key: string]: { hidden: boolean; order: number } }) => {
      const newLabeledFields = Object.keys(visibleFields)
        .filter(columnKey => !visibleFields[columnKey].hidden) // Only include non-hidden fields
        .map(columnKey => {
          const backendField = Object.keys(fieldMappingRemainder).find(
            key => fieldMappingRemainder[key].columnKey === columnKey
          );
          return backendField || null;
        })
        .filter((field): field is string => field !== null) // Filter out null values
        .join(','); // Join into a comma-separated string

      // Only update state if newLabeledFields is different
      setLabeledVisibleFields((prev: any) => {
        const isEqual = prev === newLabeledFields;
        if (!isEqual) {
          return newLabeledFields;
        }
        return prev;
      });
    },
    [fieldMappingRemainder]
  );

  const { data, refetch, isFetching, isLoading } = useQuery(
    [
      'GET_REPORTS_REMAINING',
      debouncedSearchValue,
      debouncedBarcode,
      currentPage
    ],
    async () => {
      pageToOffset(currentPage, 10);

      const res = await reportInvoiceService
        .getReportRemainingList({
          limit: 10,
          offset: pageToOffset(currentPage, 10),
          name: debouncedSearchValue,
          barcode: debouncedBarcode,
          currency_pid: Object.keys(getFilterData).length === 0 && currencyId,
          branch_pid: Object.keys(getFilterData).length === 0 && branchId,
          // is_group: savedValues?.length === 0 ? true : undefined,
          is_group: true,
          is_negative: savedValues?.length === 0 ? true : undefined,
          ...getFilterData
        })
        .then((res: any) => {
          setTotalQuantity(res?.total_quantity);
          setTotalincome(res?.total_income_amount);
          setTotalSale(res?.total_sale_amount);
          setPageCount(Math.ceil(res?.count / 10));

          return res?.products;
        });
      return res;
    },
    {
      enabled:
        debouncedSearchValue.length === 0 ||
        debouncedSearchValue.length > 2 ||
        debouncedBarcode.length === 0 ||
        debouncedBarcode.length > 2 ||
        !!currencyId ||
        !!branchId ||
        !!getFilterData ||
        !!currentPage
    }
  );

  const loading = isLoading || isFetching;

  useEffect(() => {
    if (!data || data.length === 0) {
      setGeneratedTableData([]); // Set empty array if no data
      return;
    }

    if (data) {
      const resData =
        data?.map((items: any) => {
          const packageQuantity = items?.package_quantity || 1;

          const quantity = items?.quantity
            ? Number(items?.quantity.toFixed(3))
            : 0;
          const fullPackages = Math.floor(quantity / packageQuantity);
          const remainingQuantity = quantity % packageQuantity;
          const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

          const sum =
            items?.measure_unit_kind_pid === 1
              ? items?.quantity * (items?.price / packageQuantity)
              : items?.quantity * items?.price;

          return {
            product_name: items?.name,
            barcode: items?.barcode,
            branch: branchName,
            unit: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
            currency: items?.currency?.name?.[i18n?.language],
            category: items?.nomenclature?.category?.name?.[i18n?.language],
            /* prettier-ignore */
            quantity:
              items?.measure_unit_kind_pid === 1
                ? quantityDisplay
                : items?.quantity
                  ? Number(items?.quantity.toFixed(3))
                  : 0,

            quantity_in: items?.quantity ?? 0,
            package_quantity: items?.package_quantity,
            measure_unit_id: items?.items?.measure_unit_id,
            price: items?.price ?? 0,
            price_in: items?.price_in ?? 0,
            cost: items?.price
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(items?.price)
                  .replace(/,/g, ' ')
              : 0,
            cost_in: items?.price_in
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(items?.price_in)
                  .replace(/,/g, ' ')
              : 0,
            sum: sum
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(sum)
                  .replace(/,/g, ' ')
              : 0,
            serial_number: items?.partion_codes
          };
        }) ?? [];

      if (resData) {
        setGeneratedTableData(resData);
      }
    }
  }, [data, setGeneratedTableData, t, i18n?.language, branchName]);

  const remainderDataTableColumns: ColumnDef<RemainderData>[] = [
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10; // Calculate the order number based on the row index
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },
    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'unit',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'branch',
      header: t('branch'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'category',
      header: t('category'),
      meta: {
        cellProps: { className: 'text-900', style: { whiteSpace: 'nowrap' } }
      }
    },
    {
      accessorKey: 'quantity',
      header: t('quantity'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'cost_in',
      header: t('total_price_admission'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'cost',
      header: t('price'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },

    {
      accessorKey: 'sum',
      header: t('in_amount_of'),
      meta: {
        cellProps: { className: 'text-900', style: { textAlign: 'right' } }
      }
    },
    {
      accessorKey: 'serial_number',
      header: t('serial_number'),
      cell: ({ row: { original } }) => {
        return (
          <div>
            {original.serial_number?.length > 0 && (
              <Button
                variant="soft-primary"
                onClick={() => {
                  setShowSerialNumber(true);
                  setSelectedRow(original);
                }}
              >
                {t('show')}
              </Button>
            )}
          </div>
        );
      },
      meta: {
        cellProps: {
          className: 'text-900',
          style: { whiteSpace: 'nowrap' }
        }
      }
    }
  ];

  const table = useAdvanceTable({
    data: generateTableData,
    columns: remainderDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  useEffect(() => {
    if ((getName || getBarcode) && !hasReset) {
      setCurrentPage(1);
      setHasReset(true); // Prevents multiple resets
    }
  }, [getName, getBarcode, hasReset]);

  useEffect(() => {
    // Ensure currentPage is updated
    refetch();
  }, [debouncedSearchValue, debouncedBarcode, currentPage]);

  return (
    <AdvanceTableProvider {...table}>
      <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          isLoading={loading}
          onVisibleFieldsChange={handleVisibleFieldsChange}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
      <Offcanvas
        show={showSerialNumber}
        onHide={() => {
          setShowSerialNumber(false);
        }}
        placement="end"
        backdrop="static"
        keyboard={false}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
            {t('serial_number')}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <ShowSerialNumber selectedRow={selectedRow} />
        </Offcanvas.Body>
      </Offcanvas>
    </AdvanceTableProvider>
  );
};

export default RemainderTable;
